/**
 * Types for the CampaignManagement component redesign
 * See docs/CAMPAIGN_MANAGEMENT_CARD_REDESIGN.md for full design spec
 */

import type { Campaign, AdGroup, AssetGroup } from '@/types/campaign';
import type { RDACacheEntry } from '@/hooks/useRDACache';
import type { DemandGenCacheEntry } from '@/hooks/useDemandGenCache';
import type { GetRDAResponse, GetDemandGenAdResponse } from '@/services/api/assets';

// Editor state for RSA/RDA editing
export interface EditorState {
  adGroupId: string;
  adId: string;
}

// Editor callbacks
export interface EditorCallbacks {
  onEditorSaved: () => void;
  onEditorCancel: () => void;
}

// Entity tab types - available tabs within an expanded entity card
// Note: 'search-terms' moved to Performance Metrics card
export type EntityTab = 'keywords' | 'assets' | 'rsas' | 'rdas' | 'negative-keywords' | 'channels' | 'demand-gen-ads' | 'audiences';

// Entity types
export type EntityType = 'ad_group' | 'asset_group';

// Summary stats for entity card header
export interface EntitySummary {
  keywordCount: number;
  rsaCount: number;
  rdaCount: number;
  demandGenAdCount?: number;
  assetCount: number;
  imageAssetCount: number;
  negativeKeywordCount: number;
}

// Summary stats for campaign level card header
export interface CampaignLevelSummary {
  sitelinkCount: number;
  calloutCount: number;
  snippetCount: number;
  imageCount: number;
}

// Props for CampaignManagement (main orchestrator)
export interface CampaignManagementProps {
  campaign: Campaign;
  hasFullStructure: boolean;
  selectedLandingPages: Map<string, boolean>;
  rdaCache: Record<string, RDACacheEntry>;
  isLoadingRDAs: boolean;
  demandGenCache: Record<string, DemandGenCacheEntry>;
  isLoadingDemandGenAds: boolean;
  onCreateDemandGenAd?: () => void;
}

// Props for CampaignLevelCard
export interface CampaignLevelCardProps {
  campaign: Campaign;
  isExpanded: boolean;
  onToggle: () => void;
  selectedLandingPages: Map<string, boolean>;
}

// Props for EntityCardList
export interface EntityCardListProps {
  entities: Array<AdGroup | AssetGroup>;
  entityType: EntityType;
  expandedId: string | null;
  onExpandEntity: (id: string | null) => void;
  activeTabByEntity: Record<string, EntityTab>;
  onTabChange: (entityId: string, tab: EntityTab) => void;
  campaign: Campaign;
  selectedLandingPages: Map<string, boolean>;
  rdaCache: Record<string, RDACacheEntry>;
  isLoadingRDAs: boolean;
  demandGenCache: Record<string, DemandGenCacheEntry>;
  isLoadingDemandGenAds: boolean;
  onEditRSA?: (adGroupId: string, adId: string) => void;
  onEditRDA?: (adGroupId: string, adId: string) => void;
  onEditDemandGenAd?: (adGroupId: string, adId: string) => void;
  onCreateDemandGenAd?: () => void;
  // Editor state for inline editing
  editingRSA?: EditorState | null;
  editingRDA?: EditorState | null;
  editingDemandGenAd?: EditorState | null;
  memberUuid?: string | null;
  getRDAInitialData?: (adId: string) => GetRDAResponse | undefined;
  getDemandGenAdInitialData?: (adId: string) => GetDemandGenAdResponse | undefined;
  onEditorSaved?: () => void;
  onEditorCancel?: () => void;
}

// Props for EntityCard
export interface EntityCardProps {
  entity: AdGroup | AssetGroup;
  entityType: EntityType;
  isExpanded: boolean;
  onToggle: () => void;
  activeTab: EntityTab;
  onTabChange: (tab: EntityTab) => void;
  campaign: Campaign;
  selectedLandingPages: Map<string, boolean>;
  rdaCache: Record<string, RDACacheEntry>;
  isLoadingRDAs: boolean;
  demandGenCache: Record<string, DemandGenCacheEntry>;
  isLoadingDemandGenAds: boolean;
  onEditRSA?: (adGroupId: string, adId: string) => void;
  onEditRDA?: (adGroupId: string, adId: string) => void;
  onEditDemandGenAd?: (adGroupId: string, adId: string) => void;
  onCreateDemandGenAd?: () => void;
  // Editor state for inline editing
  editingRSA?: EditorState | null;
  editingRDA?: EditorState | null;
  editingDemandGenAd?: EditorState | null;
  memberUuid?: string | null;
  getRDAInitialData?: (adId: string) => GetRDAResponse | undefined;
  getDemandGenAdInitialData?: (adId: string) => GetDemandGenAdResponse | undefined;
  onEditorSaved?: () => void;
  onEditorCancel?: () => void;
}

// Props for EntityTabBar
export interface EntityTabBarProps {
  availableTabs: EntityTab[];
  activeTab: EntityTab;
  onChange: (tab: EntityTab) => void;
  counts: Partial<Record<EntityTab, number>>;
}

// Props for panel components
export interface KeywordsPanelProps {
  adGroup: AdGroup;
  campaign: Campaign;
  selectedLandingPages: Map<string, boolean>;
}

export interface AssetsPanelProps {
  entity: AdGroup | AssetGroup;
  entityType: EntityType;
  campaign: Campaign;
  selectedLandingPages: Map<string, boolean>;
  rdaCache: Record<string, RDACacheEntry>;
  isLoadingRDAs: boolean;
}

export interface RSAPanelProps {
  adGroup: AdGroup;
  campaign: Campaign;
  onEditRSA: (adId: string) => void;
}

export interface RDAPanelProps {
  adGroup: AdGroup;
  campaign: Campaign;
  rdaCache: Record<string, RDACacheEntry>;
  isLoadingRDAs: boolean;
  onEditRDA: (adId: string) => void;
}

export interface NegativeKeywordsPanelProps {
  campaign: Campaign;
}

// Tab label configuration
export const TAB_LABELS: Record<EntityTab, string> = {
  'keywords': 'Keywords',
  'assets': 'Assets',
  'rsas': 'RSAs',
  'rdas': 'RDAs',
  'negative-keywords': 'Negative Keywords',
  'channels': 'Channels',
  'demand-gen-ads': 'Ads',
  'audiences': 'Audiences',
};

// Demand Gen channel settings
export interface DemandGenChannelSettings {
  youtubeInStream: boolean;
  youtubeInFeed: boolean;
  youtubeShorts: boolean;
  discover: boolean;
  gmail: boolean;
  display: boolean;
  // maps: boolean;  // Not yet supported in Google Ads API v22/v23
}

// Props for ChannelsPanel
export interface ChannelsPanelProps {
  adGroup: AdGroup;
  campaign: Campaign;
}

// Props for DemandGenAdsPanel
export interface DemandGenAdsPanelProps {
  adGroup: AdGroup;
  campaign: Campaign;
  demandGenCache: Record<string, DemandGenCacheEntry>;
  isLoadingDemandGenAds: boolean;
  onCreateAd?: () => void;
  onEditAd?: (adId: string) => void;
}
